/* eslint-disable no-unused-vars */
import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import Typography from "../../common-components/Typography";
import { VIDEO_RESIZE_DETAILS, PLAN } from "../../constants";
import {
  IconSVG,
  ResizeDropdown,
  ResizeDropdownLi,
  ToolbarHeader,
  ToolbarWrap,
} from "./shorts-components";
import vmTheme from "../../constants/theme";
import { font } from "../../constants/font";
import { resizeVideo } from "../../redux/actions/appUtils";
import content from "../../constants/content";
import ShortsPreset from "./shorts-preset";
import Backdrop from "../../common-components/BackDrop";
import contextActionType from "./context-action-type";
import { useShortsContext } from "./shortsContextApi";
import GroupedDropdown from "../../common-components/GroupedDropdown"; // ← adjust path as needed
import { STATIC_PATH } from "../../constants/config";
import { Divider } from "./shorts-list";
import useMobileDetection from "../../helper/hooks/useMobileDetection";

const { SET_STYLES_OPEN } = contextActionType;

/* ─────────────────────────────────────────────
   TOOLBAR LAYOUT
───────────────────────────────────────────── */

const ToolbarRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  background: #ffffff;
  border-bottom: 1px solid #f1f5f9;
`;

const RegenerateBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: transparent;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  color: #1e293b;
  transition: background 0.15s;

  &:hover {
    background: #f8fafc;
  }

  img {
    width: 20px;
    height: 20px;
  }
`;

/* ─────────────────────────────────────────────
   SORT SECTIONS CONFIG
───────────────────────────────────────────── */

const SORT_SECTIONS = [
  {
    id: "sortBy",
    title: "Sort by",
    options: [
      { value: "score", label: "Score" },
      { value: "duration", label: "Duration" },
    ],
  },
  {
    id: "direction",
    title: "Direction",
    options: [
      { value: "asc", label: "Ascending" },
      { value: "desc", label: "Descending" },
    ],
  },
];

/* ─────────────────────────────────────────────
   FILTER SECTIONS CONFIG
   Replace icon paths with your actual image paths.
   e.g. `${STATIC_PATH}yt-icon.svg`
───────────────────────────────────────────── */

const FILTER_SECTIONS = [
  {
    id: "platforms",
    title: "Platforms",
    options: [
      {
        value: "all",
        label: "All platforms",
        icon: "all-platform.svg",
        selectedIcon: "all-platform-selected.svg",
        iconAlt: "All platforms",
      },
      {
        value: "youtube",
        label: "YouTube",
        icon: "vm-youtubes.svg",
        iconAlt: "YouTube",
      },
      {
        value: "instagram",
        label: "Instagram",
        icon: "instagram.svg",
        iconAlt: "Instagram",
      },
      {
        value: "tiktok",
        label: "TikTok",
        icon: "tiktok.svg",
        iconAlt: "TikTok",
      },
      {
        value: "linkedin",
        label: "LinkedIn",
        icon: "linkedin.svg",
        iconAlt: "LinkedIn",
      },
    ],
  },
  {
    id: "type",
    title: "Type",
    options: [
      { value: "all", label: "All" },
      { value: "sequence", label: "Sequence" },
      { value: "non-sequence", label: "Non-Sequence" },
    ],
  },
];


const ShortsToolbar = () => {
  const dropdownRef = useRef(null);
  const theme = useSelector((state) => state.app.get("theme"));
  const projectDetails = useSelector((store) => store.projectDetails);
  const hasSubtitle = !!projectDetails.get("localSubtitle").size;
  const RESIZETHEME = VIDEO_RESIZE_DETAILS(theme);
  const userDetails = useSelector((store) => store.userDetails);
  const dispatch = useDispatch();
  const isMobile = useMobileDetection();

  const [selectedOption, setSelectedOption] = useState(
    RESIZETHEME[userDetails.dimensionName]?.name
  );
  const [selectedIcon, setSelectedIcon] = useState(
    RESIZETHEME[userDetails.dimensionName]?.src
  );
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [isShowModal, setIsShowModal] = useState(false);
  const { setShortsContext } = useShortsContext();
  const { permission, sourceType, plan } = userDetails;
  const [disabled, setDisabled] = useState(false);
  


  // ── Sort state ──────────────────────────────
  const [sortValues, setSortValues] = useState({
    sortBy: "score",
    direction: "desc",
  });

  // ── Filter state ─────────────────────────────
  const [filterValues, setFilterValues] = useState({
    platforms: "all",
    type: "all",
  });

  const closeModal = () => setIsShowModal(false);

  useEffect(() => {
    if (projectDetails.get("shorts").size > 0) {
      const shorts = projectDetails.get("shorts").toJS();
      const hasNoAudio = Object.values(shorts).some((short) => short.clip_type.indexOf('fallback') > -1);
      setDisabled(hasNoAudio);
    }
  }, [projectDetails]);

  const resizeProject = (name) => {
    resizeVideo(
      name,
      projectDetails,
      userDetails.projectId,
      projectDetails.get("duration"),
      dispatch
    );
    setDropdownVisible(false);
    setSelectedOption(RESIZETHEME[name].name);
    setSelectedIcon(RESIZETHEME[name].src);
  };

  useEffect(() => {
    setSelectedOption(RESIZETHEME[userDetails.dimensionName]?.name);
    setSelectedIcon(RESIZETHEME[userDetails.dimensionName]?.src);
  }, [RESIZETHEME, userDetails]);

  const calculatePosition = () => {
    if (dropdownRef.current) {
      const node = dropdownRef.current.getBoundingClientRect();
      setPosition({ top: node.bottom, left: node.left });
    }
  };

  useEffect(() => {
    if (dropdownVisible) calculatePosition();
  }, [dropdownVisible]);

  const toggleDropdown = () => setDropdownVisible((prev) => !prev);

  const openPreset = () => {
    if (hasSubtitle) {
      setIsShowModal(true);
      setShortsContext(SET_STYLES_OPEN, true);
    }
  };

  // ── Sort selection handler ───────────────────
  const handleSortSelect = (sectionId, option) => {
    const newSortValues = { ...sortValues, [sectionId]: option.value };
    setSortValues(newSortValues);
    setShortsContext("SET_SORT_VALUES", newSortValues);
  };

  // ── Filter selection handler ─────────────────
  const handleFilterSelect = (sectionId, option) => {
    const newFilterValues = { ...filterValues, [sectionId]: option.value };
    setFilterValues(newFilterValues);
    setShortsContext("SET_FILTER_VALUES", newFilterValues);
  };

  return (
    <>
      {/* {permission !== 0 && ( */}
        <ToolbarHeader>
          <Typography innerContent={"Short Clips"} font={font.boldBase_21} margin="0px 16px 0px" display={"block"} />
          <ToolbarRow>
            {/* ── SORT DROPDOWN ─────────────────────── */}
            <GroupedDropdown
              triggerLabel="Sort"
              triggerIcon={`sort.svg`}   // ← your sort icon path
              triggerIconAlt="Sort"
              sections={SORT_SECTIONS}
              selectedValues={sortValues}
              onSelect={handleSortSelect}
              width={180}
              borderRadius="8px"
              disabled={sourceType === 'shorts-dummy' || plan === PLAN.LITE || disabled}
            />

            {/* ── FILTER DROPDOWN ───────────────────── */}
            <GroupedDropdown
              triggerLabel="Filter"
              triggerIcon={`filter_list.svg`} // ← your filter icon path
              triggerIconAlt="Filter"
              sections={FILTER_SECTIONS}
              selectedValues={filterValues}
              onSelect={handleFilterSelect}
              width={180}
              maxHeight={isMobile ? "min(385px, 58vh)" : "380px"}
              iconSize="22px"
              iconRadius="4px"
              borderRadius="8px"
              disabled={sourceType === 'shorts-dummy'|| plan === PLAN.LITE || disabled}
            />
            <Divider style={{visibility: "hidden"}}/>
            {/* ── REGENERATE BUTTON ─────────────────── */}
            {!isMobile && !disabled && (
              <RegenerateBtn style={{visibility: "hidden"}}>
                <img src={`${STATIC_PATH}refresh-icon.svg`} alt="Regenerate" />
                Regenerate
              </RegenerateBtn>
            )}

            {/* Existing resize dropdown (kept intact) */}
            {dropdownVisible && (
              <>
                <Backdrop
                  onClick={() => setDropdownVisible(false)}
                  zIndex="8"
                  height="100%"
                />
                <ResizeDropdown
                  className="resizeMenu"
                  style={{
                    position: "absolute",
                    top: position.top,
                    left: position.left,
                    zIndex: 1000,
                  }}
                >
                  {Object.keys(RESIZETHEME).map((key) => {
                    const isSelected = key === userDetails.dimensionName;
                    return (
                      <ResizeDropdownLi key={key} isHovered={isSelected}>
                        <IconSVG
                          isSelected={isSelected}
                          dangerouslySetInnerHTML={{
                            __html: isSelected
                              ? RESIZETHEME[key].selectedSrc
                              : RESIZETHEME[key].src,
                          }}
                        />
                        <Typography
                          content={RESIZETHEME[key].title}
                          display="block"
                          color={vmTheme[theme].primaryColor}
                          padding="10px"
                          font={font.normalBase_21}
                          cursor="pointer"
                          onClick={() => resizeProject(key)}
                          isHovered={isSelected}
                          isSelected={key === userDetails.dimensionName}
                        />
                      </ResizeDropdownLi>
                    );
                  })}
                </ResizeDropdown>
              </>
            )}

            <Typography
              content={content.SUBTITLE_STYLES}
              cursor={hasSubtitle ? "pointer" : "not-allowed"}
              padding="5px 20px 5px 5px"
              className="subtitle-style"
              onClick={() => openPreset()}
              display={"none"}
            />
          </ToolbarRow>
        </ToolbarHeader>
      {/* )} */}
      <ShortsPreset
        isShowModal={isShowModal}
        onClose={closeModal}
        preventEscClose
        isDisabled
      />
    </>
  );
};

export default ShortsToolbar;